/**
 * AmenitySelector - Multi-select amenity picker for meeting points
 *
 * Allows guides to select available amenities at meeting point:
 * - Parking (free/paid)
 * - Restrooms
 * - Cell Service
 * - Water
 * - Food Nearby
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor with GPX Intelligence
 * Story: SCRUM-235 (US-1.20.6-S.2-02: Amenity Selector Component)
 *
 * @component
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Car, DollarSign, Building, Phone, Droplets, UtensilsCrossed } from 'lucide-react';

/**
 * Available amenities with Lucide icons and labels
 * Icons match those used in AmenityTag component for consistency
 */
const AMENITIES = [
  {
    id: 'parking',
    icon: Car,
    label: 'Free Parking',
    description: 'Free parking available'
  },
  {
    id: 'paid_parking',
    icon: DollarSign,
    label: 'Paid Parking',
    description: 'Paid parking available'
  },
  {
    id: 'restrooms',
    icon: Building,
    label: 'Restrooms',
    description: 'Restroom facilities available'
  },
  {
    id: 'cell_service',
    icon: Phone,
    label: 'Cell Service',
    description: 'Reliable cell service'
  },
  {
    id: 'water',
    icon: Droplets,
    label: 'Water',
    description: 'Drinking water available'
  },
  {
    id: 'food',
    icon: UtensilsCrossed,
    label: 'Food Nearby',
    description: 'Food/restaurants within walking distance'
  }
];

/**
 * AmenitySelector
 *
 * Multi-select grid of amenity badges.
 */
const AmenitySelector = ({ selected = [], onChange }) => {
  const handleToggle = (amenityId) => {
    const isSelected = selected.some(a => a.id === amenityId || a === amenityId);

    if (isSelected) {
      // Remove amenity
      onChange(selected.filter(a => (a.id || a) !== amenityId));
    } else {
      // Add amenity
      const amenity = AMENITIES.find(a => a.id === amenityId);
      // Store the icon name as string for serialization
      onChange([...selected, { id: amenity.id, label: amenity.label, icon: amenity.id }]);
    }
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {AMENITIES.map((amenity) => {
        const isSelected = selected.some(a => (a.id || a) === amenity.id);
        const IconComponent = amenity.icon;

        return (
          <button
            key={amenity.id}
            type="button"
            onClick={() => handleToggle(amenity.id)}
            className={`
              relative p-4 border-2 rounded-lg text-left transition-all
              ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }
            `}
            title={amenity.description}
          >
            {/* Checkmark */}
            {isSelected && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            )}

            {/* Icon */}
            <div className="mb-2 flex items-center justify-center">
              <IconComponent
                size={28}
                className={`
                  ${isSelected ? 'text-blue-600' : 'text-gray-600'}
                  ${isSelected ? 'opacity-100' : 'opacity-60'}
                `}
              />
            </div>

            {/* Label */}
            <div
              className={`
                text-sm font-medium
                ${isSelected ? 'text-blue-900' : 'text-gray-700'}
              `}
            >
              {amenity.label}
            </div>
          </button>
        );
      })}
    </div>
  );
};

AmenitySelector.propTypes = {
  /**
   * Selected amenities (array of objects with id, label, icon)
   */
  selected: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        icon: PropTypes.string
      })
    ])
  ),

  /**
   * Change handler
   */
  onChange: PropTypes.func.isRequired
};

export default AmenitySelector;
